import * as React from 'react';

import { transparentize } from 'polished';
import styled from 'styled-components';

import { COLOR, FONT, FONT_SIZE, SPACING } from '../../../../packages/constants';
import { ChevronRight } from '../../../../packages/icons/';

interface ChartGroupProps {
    onChange?: (e: any) => void;
    chartData?: any;
    hideFavourites?: boolean;
    hideRecent?: boolean;
    checked?: any;
    assistAccess?: boolean;
}

interface DataTestIdProps {
    dataTestId: string;
}

interface StyledGroupItemProps {
    children: React.ReactNode;
    checked: boolean;
    radioValue: string;
    assistAccess?: boolean;
    dataTestId?: string;
    onChange?: (e: any) => void;
    onClick?: (e: any) => void;
    ref?: any;
}

interface StyledInputProps {
    assistAccess?: boolean;
    ref?: any;
    rel?: string;
    autoComplete?: string;
    dataTestId?: string;
}

interface StyledLabelTextProps {
    checked?: boolean;
    assistAccess?: boolean;
}

export const ChartGroupScroller = styled.div`
    margin-top: ${SPACING.LARGE};
    height: 100%;
    overflow: auto;
    padding-top: 5px;
    padding-left: ${SPACING.SMALL};
`;

export const ChartGroupTitle = styled.h3`
    font-weight: 700;
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    color: #6f788a;
    padding: 5px;
    margin: 0;
    margin-top: ${SPACING.MEDIUM};
`;

const ClickableLi = styled.li.attrs<DataTestIdProps>(({ dataTestId }) => ({
    'data-test-id': dataTestId,
}))``;

export const StyledClickableListItem = styled(ClickableLi)<DataTestIdProps>`
    list-style-type: none;
    margin: 0;
    padding: 0;
    line-height: 1;
    data-test-id: ${(props: DataTestIdProps): string => props.dataTestId};
`;

export const StyledListItem = styled.li`
    list-style-type: none;
    margin: 0;
    padding: 0;
    line-height: 1;
`;

export const StyledList = styled.ul`
    list-style-type: none;
    margin: 0 0 ${SPACING.MEDIUM};
    padding: 0;

    ${StyledClickableListItem} {
        margin-right: ${SPACING.MEDIUM};

        ${StyledClickableListItem} {
            margin-right: 0;
        }
    }
`;

const ChartGroupWrapperPaddingTop = `26px`;

export const ChartGroupWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${COLOR.MODAL.MODAL_PANEL_ACCENT};
    padding: ${ChartGroupWrapperPaddingTop} 0 0 ${SPACING.SMALL};
    width: 240px;
    height: calc(100% - ${ChartGroupWrapperPaddingTop});

    > ${StyledList}:first-child {
        margin-left: ${SPACING.SMALL};
    }
`;

export const HorizontalRule = styled.hr`
    border: 0;
    border-bottom: 1px solid #b2bccd;
    width: calc(100% - ${SPACING.X_LARGE});
    margin: ${SPACING.MEDIUM} ${SPACING.X_LARGE} ${SPACING.MEDIUM} ${SPACING.SMALL};
`;

const StyledLabel = styled.label`
    background-color: ${COLOR.MODAL.MODAL_PANEL_ACCENT};
    position: relative;
`;

const StyledLabelText = styled.div<StyledLabelTextProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: ${FONT.OPEN_SANS};
    font-size: ${FONT_SIZE.SMALL};
    font-weight: 400;
    background-color: ${(props: StyledLabelTextProps): string => (props.checked ? COLOR.PRIMARY.WHITE : 'transparent')};
    color: ${COLOR.BRAND.REPORTING};
    padding: 5px;
    cursor: pointer;

    &:hover {
        background-color: ${(props: StyledLabelTextProps): string =>
            props.checked ? COLOR.PRIMARY.WHITE : transparentize('0.5', COLOR.PRIMARY.WHITE)};
    }
`;

const StyledChevron = styled(ChevronRight)`
    fill: #b2bccd;
    height: 12px;
    width: 12px;
    opacity: ${(props: StyledLabelTextProps): number => (props.checked ? 1 : 0)};
`;

const StyledRadio = styled.input<StyledInputProps>`
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    z-index: -1;

    &:focus ~ ${StyledLabelText} {
        outline: ${(props: StyledInputProps): string => (props.assistAccess ? '1px dotted #212121' : '0')};
        outline: ${(props: StyledInputProps): string =>
            props.assistAccess ? '5px auto -webkit-focus-ring-color' : '0'};
    }
`;

export const StyledGroupItem = React.forwardRef<HTMLInputElement, StyledGroupItemProps>(
    ({ children, onChange, onClick, radioValue, checked, assistAccess, dataTestId, ...props }, ref) => (
        <StyledClickableListItem onClick={onClick} dataTestId={dataTestId ?? ''} {...props}>
            <StyledLabel>
                <StyledRadio
                    assistAccess={assistAccess}
                    type={'radio'}
                    name={'chart-group'}
                    ref={ref}
                    value={radioValue}
                    onChange={onChange}
                    checked={checked}
                />
                <StyledLabelText assistAccess={assistAccess} checked={checked}>
                    {children} <StyledChevron checked={checked} />
                </StyledLabelText>
            </StyledLabel>
        </StyledClickableListItem>
    ),
);
StyledGroupItem.displayName = 'StyledGroupItem';

const ChartGroup = React.forwardRef<HTMLDivElement, ChartGroupProps>(
    ({ onChange, chartData, hideFavourites, hideRecent, checked, assistAccess, ...props }, ref) => (
        <ChartGroupWrapper ref={ref} {...props}>
            <StyledList>
                <StyledGroupItem
                    onChange={onChange}
                    radioValue={'create-a-new-chart'}
                    assistAccess={assistAccess}
                    checked={checked}
                    dataTestId={'chart-group-name__create-a-new-chart'}
                >
                    Create a new chart
                </StyledGroupItem>
                {!hideRecent && (
                    <StyledGroupItem
                        onChange={onChange}
                        radioValue={'recent'}
                        assistAccess={assistAccess}
                        checked={checked}
                        dataTestId={'chart-group-name__recent'}
                    >
                        Recent
                    </StyledGroupItem>
                )}
                {!hideFavourites && (
                    <StyledGroupItem
                        onChange={onChange}
                        radioValue={'favourites'}
                        assistAccess={assistAccess}
                        checked={checked}
                        dataTestId={'chart-group-name__favourites'}
                    >
                        Favourites
                    </StyledGroupItem>
                )}
            </StyledList>

            <HorizontalRule />
            <ChartGroupScroller>
                <StyledList>
                    <StyledGroupItem
                        onChange={onChange}
                        radioValue={'all'}
                        assistAccess={assistAccess}
                        checked={checked}
                        dataTestId={'chart-group-name__all'}
                    >
                        All...
                    </StyledGroupItem>
                    {chartData &&
                        Object.keys(chartData).map((groupTitle) => {
                            return (
                                <StyledListItem key={`group-title-${groupTitle}`}>
                                    <ChartGroupTitle>{chartData[groupTitle].title}</ChartGroupTitle>
                                    <StyledList>
                                        {Object.keys(chartData[groupTitle].groups).map((groupItem) => {
                                            return (
                                                <StyledGroupItem
                                                    onChange={onChange}
                                                    radioValue={chartData[groupTitle].groups[groupItem].id}
                                                    key={`group-item-${groupItem}`}
                                                    assistAccess={assistAccess}
                                                    checked={checked}
                                                    dataTestId={`chart-group-name__${chartData[groupTitle].groups[groupItem].iconName}`}
                                                >
                                                    {chartData[groupTitle].groups[groupItem].title}
                                                </StyledGroupItem>
                                            );
                                        })}
                                    </StyledList>
                                </StyledListItem>
                            );
                        })}
                </StyledList>
            </ChartGroupScroller>
        </ChartGroupWrapper>
    ),
);
ChartGroup.displayName = 'ChartGroup';

export default ChartGroup;
